import { useCallback, useEffect, useReducer } from 'react';

import {
  defaultState,
  reducer,
  setContentVisible,
  setDrawerConfig,
  setDrawerContent,
  setIsOpen,
  setMode,
  setPosition,
  setState,
} from '../drawer.store';
import {
  DrawerConfig,
  DrawerContent,
  DrawerMode,
  DrawerPosition,
  DrawerProps,
  DrawerState,
} from '../drawer.types';

const DEFAULT_CLOSE_ANIMATION_MS = 300;

export const useDrawerInit = (props: DrawerProps) => {
  const initialState: DrawerState = {
    ...defaultState,
    isOpen: props.isOpen ?? defaultState.isOpen,
    config: {
      ...defaultState.config,
      position: props.position ?? defaultState.config.position,
      mode: props.mode ?? defaultState.config.mode,
      hasBackdrop: !!props.hasBackdrop,
    },
  };

  const [drawerState, dispatch] = useReducer(reducer, initialState);
  const { config } = drawerState;
  const { onClose, closeAnimationDurationMs = DEFAULT_CLOSE_ANIMATION_MS } = config;

  const setConfig = useCallback(
    (config: Partial<DrawerConfig>) => {
      dispatch(setDrawerConfig(config));
    },
    [dispatch],
  );

  const toggle = useCallback(
    (value?: boolean) => {
      dispatch(setIsOpen(value));
    },
    [dispatch],
  );

  const toggleMode = useCallback(
    (mode?: DrawerMode) => {
      dispatch(setMode(mode));
    },
    [dispatch],
  );

  const togglePosition = useCallback(
    (position?: DrawerPosition) => {
      dispatch(setPosition(position));
    },
    [dispatch],
  );

  const setContent = useCallback(
    (content: Partial<DrawerContent>) => {
      dispatch(setDrawerContent(content));
    },
    [dispatch],
  );

  const configureDrawer = useCallback(
    (state: Partial<DrawerState>) => {
      dispatch(setState(state));
    },
    [dispatch],
  );

  const handleClose = useCallback(
    (event?: React.SyntheticEvent) => {
      dispatch(setIsOpen(false));
      onClose?.(event);
    },
    [dispatch, onClose],
  );

  const handleCloseClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
      handleClose(event);
    },
    [handleClose],
  );

  // Keep content mounted while the close animation runs to avoid abrupt visual jumps.
  useEffect(() => {
    if (drawerState.isOpen) {
      dispatch(setContentVisible(true));
      return;
    }

    const timeoutId = setTimeout(() => {
      dispatch(setContentVisible(false));
    }, closeAnimationDurationMs);

    return () => clearTimeout(timeoutId);
  }, [closeAnimationDurationMs, dispatch, drawerState.isOpen]);

  return {
    drawerState,
    toggle,
    toggleMode,
    togglePosition,
    configureDrawer,
    setConfig,
    setContent,
    handleClose,
    handleCloseClick,
  };
};
